import "react-tooltip/dist/react-tooltip.css";

import cx from "classnames";
import Table from "components/Table/Table";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import { useState } from "react";
import { Tooltip } from "react-tooltip";
import type { FeedImportLogDataType } from "types/DataTypes";
import { useParams } from "react-router-dom";
import { setShowClientFeedsDrawer } from "reducers/drawer";
import { useGetClientFeedsByIdQuery } from "services/client-api";
import { useToken } from "utils/context";
import { useDispatch, useSelector } from "utils/redux";
import KaboodleToaster from "components/common/KaboodleToaster";

const ClientFeedsDrawer = () => {
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const { clientId } = useParams<{ clientId: string }>();
  const [page, setPage] = useState(1);
  const loggedInUser = useSelector((state) => state.main.loggedInUser);

  const showClientFeedsDrawer = useSelector(
    (state) => state.drawer.showClientFeedsDrawer,
  );

  const {
    data: feeds = { results: [], count: 0 },
    isFetching,
    isLoading,
  } = useGetClientFeedsByIdQuery(
    {
      clientId,
    },
    {
      skip: !clientId,
    },
  );
  const rows = feeds.results || [];

  // Show loading state when drawer is open but data hasn't loaded yet
  const isTableLoading = setShowClientFeedsDrawer && (isFetching || isLoading);

  const FEED_COLUMNS = [
    { key: "id", name: "ID", ...commonColumnProps },
    { key: "status_verbose", name: "Status", ...commonColumnProps },
    { key: "type_verbose", name: "Type", ...commonColumnProps },
    { key: "item_count", name: "Item Count", ...commonColumnProps },
    { key: "original_filename", name: "Filename", ...commonColumnProps },
    {
      key: "progress",
      name: "Progress",
      ...commonColumnProps,
      renderCell: (props: { row: FeedImportLogDataType }) => {
        return (
          <span>{props.row.progress ? `${props.row.progress}%` : "N/A"}</span>
        );
      },
    },
    { key: "processing_started_on", name: "Started", ...commonColumnProps },
    { key: "processing_completed_on", name: "Completed", ...commonColumnProps },
    {
      key: "actions",
      name: "Actions",
      ...commonColumnProps,
      renderCell: (props: { row: FeedImportLogDataType }) => {
        const downloadReceiptFileName = `feed-log-${props.row.id}-receipt.csv`;
        const downloadExportCsvFileName = `feed-log-${props.row.id}-data-export.csv`;
        return (
          <div className="flex jc-se no-pd action-buttons">
            <div
              className="material-icons edit"
              data-tooltip-id="download-tooltip-main"
              data-tooltip-content="Download Receipt"
              data-tooltip-place="top"
              onClick={(e) => {
                e.preventDefault();

                if (!props.row.proxy_url) {
                  KaboodleToaster.error("No download receipt available.");
                  return;
                }
                KaboodleToaster.promiseLoading(
                  (async () => {
                    const proxyResponse = await fetch(props.row.proxy_url, {
                      headers: {
                        Authorization: `Bearer ${accessToken}`,
                      },
                    });

                    if (!proxyResponse.ok) {
                      throw new Error("Failed to fetch document");
                    }

                    const blob = await proxyResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = downloadReceiptFileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  })(),
                  {
                    loadingMessage: `Downloading ${downloadReceiptFileName}...`,
                    successMessage: `Downloaded ${downloadReceiptFileName}`,
                    errorMessage: "Failed to download document",
                  },
                );
              }}
            >
              archive
            </div>
            {loggedInUser?.is_superuser ? (
              <div
                className="material-icons edit"
                data-tooltip-id="download-tooltip-main"
                data-tooltip-content="Download CSV Export"
                data-tooltip-place="top"
                onClick={(e) => {
                  e.preventDefault();

                  if (!props.row.proxy_source_export_csv_url) {
                    KaboodleToaster.error(
                      "No download source export CSV available.",
                    );
                    return;
                  }
                  KaboodleToaster.promiseLoading(
                    (async () => {
                      const proxyResponse = await fetch(
                        props.row.proxy_source_export_csv_url,
                        {
                          headers: {
                            Authorization: `Bearer ${accessToken}`,
                          },
                        },
                      );

                      if (!proxyResponse.ok) {
                        throw new Error("Failed to fetch document");
                      }

                      const blob = await proxyResponse.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = downloadExportCsvFileName;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    })(),
                    {
                      loadingMessage: `Downloading ${downloadExportCsvFileName}...`,
                      successMessage: `Downloaded ${downloadExportCsvFileName}`,
                      errorMessage: "Failed to download document",
                    },
                  );
                }}
              >
                download_for_offline
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showClientFeedsDrawer,
      })}
    >
      <div className="documents-wrapper w-search">
        <div className="header-primary flex jc-sb">
          Client Import Feeds
          <a
            className="header-primary alt-btn"
            onClick={() => dispatch(setShowClientFeedsDrawer(false))}
          >
            Close
          </a>
        </div>

        <div className="data-values mrg-top">
          <Table
            columns={FEED_COLUMNS}
            rows={rows}
            rowKeyGetter={(row: FeedImportLogDataType) => row.id}
            onCellClick={() => {}}
            isLoading={isTableLoading}
            drawer={true}
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex: page,
              rowCount: feeds.count,
              onPageChange: (pageIndex) => setPage(pageIndex),
              isLoading: isTableLoading,
            }}
          />
          <Tooltip
            id="download-tooltip-main"
            className="custom-tooltip"
            place="top"
          />
        </div>
      </div>
    </div>
  );
};

export default ClientFeedsDrawer;
